import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  type OnDestroy,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MatFormField,
  MatLabel,
  MatOption,
  MatSelect,
} from '@angular/material/select';
import {
  OrganisationService,
  PracticeSelectorComponent,
} from '@principle-theorem/ng-principle-shared';
import {
  type IMatSelectOptions,
  LoaderPlaceholderComponent,
  matSelectOptions,
} from '@principle-theorem/ng-shared';
import { Organisation } from '@principle-theorem/principle-core';
import {
  type IBrand,
  type IPodiumSMSLocation,
  type IPractice,
} from '@principle-theorem/principle-core/interfaces';
import {
  type DocumentReference,
  filterUndefined,
  isSameRef,
  type WithRef,
} from '@principle-theorem/shared';
import { sortBy } from 'lodash';
import { BehaviorSubject, type Observable, ReplaySubject, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { PodiumPracticeLocationFormGroup } from '../podium-practice-location-form';

@Component({
  selector: 'pr-podium-location-input',
  templateUrl: './podium-location-input.component.html',
  styleUrls: ['./podium-location-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    PracticeSelectorComponent,
    LoaderPlaceholderComponent,
  ],
})
export class PodiumLocationInputComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  brands$: Observable<WithRef<IBrand>[]>;
  selectedPractice$ = new ReplaySubject<WithRef<IPractice>>(1);
  options$ = new ReplaySubject<IMatSelectOptions<string>>(1);
  loading$ = new BehaviorSubject<boolean>(true);
  form = new PodiumPracticeLocationFormGroup();
  locations$ = new ReplaySubject<IPodiumSMSLocation[]>(1);

  @Input()
  set locations(locations: IPodiumSMSLocation[]) {
    if (!locations) {
      return;
    }

    this.options$.next(
      matSelectOptions(
        sortBy(
          locations.map((location) => ({
            label: location.label,
            value: location.uid,
          })),
          'label'
        )
      )
    );
    this.loading$.next(false);
  }

  constructor(private _organisation: OrganisationService) {
    this.brands$ = this._organisation.organisation$.pipe(
      filterUndefined(),
      switchMap((organisation) => Organisation.brands$(organisation))
    );

    this.selectedPractice$
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((practice) =>
        this.form.controls.practiceRef.setValue(practice.ref)
      );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  isSelectedPracticeRef(
    practiceRef: DocumentReference<IPractice>,
    selectedPracticeRef: DocumentReference<IPractice>
  ): boolean {
    return isSameRef(practiceRef, selectedPracticeRef);
  }
}
